import { useEffect, useRef, useState } from 'react'
import AnimatedLetters from '../AnimatedLetters'
import gsap from 'gsap'
import profile from '../../data/profiles/activeProfile'
import './index.scss'

const { jobs, arsenal, subtitle } = profile.experience
const companyCount = new Set(jobs.map((j) => j.company)).size

const REDUCED_MOTION =
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches

/* Animate '−50%' / '10K+' style values: count the number, keep the dressing */
const countUp = (el) => {
  const raw = el.dataset.raw
  const match = raw.match(/^([^0-9]*)(\d+\.?\d*)(.*)$/)
  if (!match) return
  const [, prefix, numStr, suffix] = match
  const obj = { v: 0 }
  el.textContent = prefix + '0' + suffix
  gsap.to(obj, {
    v: parseFloat(numStr),
    duration: 0.9,
    ease: 'power2.out',
    onUpdate() {
      el.textContent = prefix + Math.round(obj.v) + suffix
    },
  })
}

const Experience = () => {
  const [letterClass, setLetterClass] = useState('text-animate')
  const pageRef = useRef(null)

  useEffect(() => {
    const t = setTimeout(() => setLetterClass('text-animate-hover'), 3000)
    return () => clearTimeout(t)
  }, [])

  // Scroll choreography — reveals, stat count-ups, and the margin rail's
  // active tick. All done with classList + GSAP: zero React re-renders.
  useEffect(() => {
    const page = pageRef.current
    if (!page) return

    const entries = Array.from(page.querySelectorAll('.xp-entry'))
    const railLinks = Array.from(page.querySelectorAll('.xp-rail__item'))
    const revealables = Array.from(page.querySelectorAll('.xp-reveal'))
    const counted = new Set()

    if (REDUCED_MOTION) {
      revealables.forEach((el) => el.classList.add('is-in'))
    }

    const revealObs = new IntersectionObserver(
      (obs) => {
        obs.forEach((o) => {
          if (!o.isIntersecting) return
          o.target.classList.add('is-in')
          if (!counted.has(o.target)) {
            counted.add(o.target)
            o.target.querySelectorAll('.xp-stat__value').forEach(countUp)
          }
          revealObs.unobserve(o.target)
        })
      },
      { threshold: 0.12 }
    )

    // Rail tick follows the last entry whose top has crossed the upper
    // third of the ledger — rAF-throttled classList writes, no re-renders
    let ticking = false
    const syncRail = () => {
      ticking = false
      const line = page.scrollTop + page.clientHeight * 0.34
      let idx = 0
      entries.forEach((el, i) => {
        if (el.offsetTop <= line) idx = i
      })
      railLinks.forEach((l, i) => l.classList.toggle('is-active', i === idx))
    }
    const onScroll = () => {
      if (ticking) return
      ticking = true
      requestAnimationFrame(syncRail)
    }

    if (!REDUCED_MOTION) {
      revealables.forEach((el) => revealObs.observe(el))
    } else {
      revealables.forEach((el) => {
        el.querySelectorAll('.xp-stat__value').forEach((v) => {
          v.textContent = v.dataset.raw
        })
      })
    }

    page.addEventListener('scroll', onScroll, { passive: true })
    syncRail()

    return () => {
      revealObs.disconnect()
      page.removeEventListener('scroll', onScroll)
    }
  }, [])

  const scrollToJob = (i) => {
    const el = pageRef.current?.querySelectorAll('.xp-entry')[i]
    el?.scrollIntoView({
      behavior: REDUCED_MOTION ? 'auto' : 'smooth',
      block: 'start',
    })
  }

  return (
    <div className="xp-page" ref={pageRef}>
      {/* ── Ghost word backdrop ── */}
      <span className="xp-ghost" aria-hidden="true">
        Career
      </span>

      {/* ── Header ── */}
      <header className="xp-header">
        <div className="xp-header__main">
          <p className="xp-kicker">
            <span className="xp-kicker__rule" />
            Career
          </p>
          <h1 className="xp-title">
            <AnimatedLetters
              letterClass={letterClass}
              strArray={"Where I've ".split('')}
              idx={8}
            />
            <em>
              <AnimatedLetters
                letterClass={letterClass}
                strArray={'worked'.split('')}
                idx={20}
              />
            </em>
          </h1>
          <p className="xp-subtitle">{subtitle}</p>
        </div>

        <div className="xp-index" aria-hidden="true">
          <div className="xp-index__item">
            <span className="xp-index__num">
              {String(companyCount).padStart(2, '0')}
            </span>
            <span className="xp-index__label">Companies</span>
          </div>
          <div className="xp-index__item">
            <span className="xp-index__num">
              {String(jobs.length).padStart(2, '0')}
            </span>
            <span className="xp-index__label">Roles</span>
          </div>
        </div>
      </header>

      {/* ── Body: margin rail + ledger feed ── */}
      <div className="xp-body">
        <nav className="xp-rail" aria-label="Jump to role">
          <span className="xp-rail__label">Index</span>
          {jobs.map((job, i) => (
            <button
              key={`${job.company}-${i}`}
              className="xp-rail__item"
              onClick={() => scrollToJob(i)}
            >
              <span className="xp-rail__tick" />
              <span className="xp-rail__name">{job.company}</span>
            </button>
          ))}
        </nav>

        <div className="xp-feed">
          {jobs.map((job, i) => (
            <article
              key={`${job.company}-${i}`}
              className="xp-entry xp-reveal"
            >
              <span className="xp-entry__ghost" aria-hidden="true">
                {String(i + 1).padStart(2, '0')}
              </span>

              <header className="xp-entry__head">
                <div className="xp-entry__title-block">
                  <h2 className="xp-entry__company">
                    {job.company}
                    {job.isCurrent && (
                      <span className="xp-entry__live" title="Current role" />
                    )}
                  </h2>
                  <p className="xp-entry__role">{job.title}</p>
                </div>
                <div className="xp-entry__when">
                  <span className="xp-entry__range">{job.range}</span>
                  <span
                    className={`xp-entry__tenure${
                      job.isCurrent ? ' xp-entry__tenure--current' : ''
                    }`}
                  >
                    {job.tenure}
                  </span>
                </div>
              </header>

              <div className="xp-stats">
                {job.metrics.map((m) => (
                  <div key={m.label} className="xp-stat">
                    <span className="xp-stat__value" data-raw={m.value}>
                      {m.value}
                    </span>
                    <span className="xp-stat__label">{m.label}</span>
                  </div>
                ))}
              </div>

              <ul className="xp-bullets">
                {job.bullets.map((b, j) => (
                  <li key={j} className="xp-bullet">
                    {typeof b === 'object' ? (
                      <>
                        <strong className="xp-bullet__lead">{b.lead}</strong>
                        {' — '}
                        {b.detail}
                      </>
                    ) : (
                      b
                    )}
                  </li>
                ))}
              </ul>

              <p className="xp-tags">
                {job.tags.map((tag, j) => (
                  <span key={tag} className="xp-tags__item">
                    {j > 0 && <span className="xp-tags__sep">/</span>}
                    {tag}
                  </span>
                ))}
              </p>
            </article>
          ))}

          {/* ── Arsenal ── */}
          <section className="xp-arsenal xp-reveal">
            <h3 className="xp-rule-title">
              <span className="xp-rule-title__index">+</span>
              <span className="xp-rule-title__text">Technical Arsenal</span>
              <span className="xp-rule-title__line" />
            </h3>

            <div className="xp-arsenal__grid">
              {arsenal.map((group) => (
                <div key={group.category} className="xp-arsenal__row">
                  <span className="xp-arsenal__cat">{group.category}</span>
                  <div className="xp-arsenal__tags">
                    {group.tags.map((tag) => (
                      <span key={tag} className="xp-arsenal__tag">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>
    </div>
  )
}

export default Experience
